import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import AppDataGrid from '@/components/common/AppDataGrid';
import { store } from '@/service/store';
import { useSignals } from "@preact/signals-react/runtime";

interface CustomerVehiclesTabProps {
  customerId: string;
}

const CustomerVehiclesTab: React.FC<CustomerVehiclesTabProps> = ({ customerId }) => {
  useSignals();

  const { t } = useTranslation();

  let customer = store.customers.get(customerId);
  const loading = store.customers.isLoading.value || customer?.cars.isLoading.value;

  useEffect(() => {
    const loadVehicles = async () => {
      if (!customer) {
        await store.customers.load();
        customer = store.customers.get(customerId);
      }
      try {
        await customer.cars.load();
      } catch (error) {
        console.error('Error loading vehicles:', error);
      }
    };

    loadVehicles();
  }, [customerId]);

  const columns: GridColDef[] = [
    {
      field: 'vin',
      headerName: t('assistant.vehicleIdentificationStep.vin'),
      flex: 2,
      minWidth: 200,
    },
    {
      field: 'make',
      headerName: t('vehicles.make'),
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'model',
      headerName: t('vehicles.model'),
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'year',
      headerName: t('vehicles.year'),
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'isVisible',
      headerName: t('vehicles.status'),
      flex: 1,
      minWidth: 100,
      valueGetter: (value: boolean) => value ? t('common.status.active') : t('common.status.inactive'),
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!customer?.cars.data.value || (customer?.cars.data.value.length ?? 0) === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">{t('vehicles.noVehicles')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <AppDataGrid
        rows={customer?.cars.data.value || []}
        columns={columns}
        density={"compact"}
        getRowId={(row) => row.uuid || row.vin}
        loading={loading}
        autoHeight={false}
        sx={{ height: '100%' }}
      />
    </Box>
  );
};

export default CustomerVehiclesTab;
